import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Paper } from '@material-ui/core';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

const CHAT_POSITION_KEY = 'deviare-chat-position';
const DEFAULT_POSITION = 'bottom-left';

const getSavedPosition = () => {
  try {
    const savedPosition = window.localStorage.getItem(CHAT_POSITION_KEY);
    if (savedPosition === 'bottom-right' || savedPosition === 'top-right') {
      return 'bottom-right';
    }
    if (savedPosition === 'bottom-left' || savedPosition === 'top-left') {
      return 'bottom-left';
    }
    return DEFAULT_POSITION;
  } catch (error) {
    return DEFAULT_POSITION;
  }
};

const useStyles = makeStyles((theme) => ({
  hitArea: {
    position: 'fixed',
    zIndex: 1400,
    boxSizing: 'content-box',
    width: 44,
    height: 44,
    '&:hover $moveButton, &:focus-within $moveButton': {
      opacity: 0.92,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
    '&:hover $moveButton:hover, &:focus-within $moveButton:focus': {
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
    [theme.breakpoints.down('xs')]: {
      width: 40,
      height: 40,
    },
  },
  reachLeft: {
    paddingLeft: 36,
  },
  reachRight: {
    paddingRight: 36,
  },
  floatingControls: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  dockBottom: {
    bottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      bottom: theme.spacing(1.5),
    },
  },
  dockLeft: {
    left: props => props.collapsedSidebarWidth ? (props.collapsedSidebarWidth - 44) / 2 : theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      left: theme.spacing(1.5),
    },
  },
  dockRight: {
    right: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(1.5),
    },
  },
  moveButton: {
    position: 'absolute',
    width: 24,
    height: 24,
    padding: 2,
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'opacity 150ms ease, visibility 150ms ease',
    color: '#2459a8',
    backgroundColor: '#f8faff',
    border: '1px solid #ccd9ec',
    borderRadius: '50%',
    boxShadow: '0 2px 6px rgba(31, 74, 135, 0.24)',
    '&:hover': {
      color: '#174985',
      backgroundColor: '#fff',
      borderColor: '#9db6da',
    },
    '& svg': {
      fontSize: 18,
    },
  },
  horizontalMoveButton: {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  moveLeft: {
    right: 'calc(100% + 6px)',
  },
  moveRight: {
    left: 'calc(100% + 6px)',
  },
  floatingButton: {
    touchAction: 'none',
    cursor: 'grab',
    userSelect: 'none',
    width: 44,
    height: 44,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: '#3C7DDD',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
    '&:hover': {
      backgroundColor: '#2f66b7',
    },
    '& svg': {
      fontSize: 22,
    },
    [theme.breakpoints.down('xs')]: {
      width: 40,
      height: 40,
      '& svg': {
        fontSize: 20,
      },
    },
  },
  chatPanel: {
    position: 'fixed',
    width: 'min(95vw, 420px)',
    height: 'min(72vh, 620px)',
    maxHeight: 'calc(100vh - 110px)',
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 1400,
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 24px)',
      height: 'min(68vh, 560px)',
      maxHeight: 'calc(100vh - 95px)',
    },
  },
  panelBottom: {
    bottom: 72,
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
    },
  },
  panelLeft: {
    left: 16,
    [theme.breakpoints.down('sm')]: {
      left: 12,
    },
  },
  panelRight: {
    right: 16,
    [theme.breakpoints.down('sm')]: {
      right: 12,
    },
  },
  closeButton: {
    position: 'absolute',
    top: 7,
    right: 8,
    zIndex: 2,
    width: 36,
    height: 36,
    padding: 6,
    color: '#fff',
    backgroundColor: 'transparent',
    '& svg': {
      fontSize: 22,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.16)',
    },
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 0,
  },
}));

const ChatbotWidget = ({ collapsedSidebarWidth = 0 }) => {
  const classes = useStyles({ collapsedSidebarWidth });
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(getSavedPosition);
  const [dragOffset, setDragOffset] = useState(null);
  const drag = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const suppressClick = useRef(false);
  const horizontalPosition = position.endsWith('right') ? 'right' : 'left';

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      if (panelRef.current?.contains(event.target) || launcherRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  const moveChat = (nextPosition) => {
    setPosition(nextPosition);
    try {
      window.localStorage.setItem(CHAT_POSITION_KEY, nextPosition);
    } catch (error) {
      // The widget can still move when browser storage is unavailable.
    }
  };

  const flipHorizontally = () => {
    moveChat(horizontalPosition === 'right' ? 'bottom-left' : 'bottom-right');
  };

  const startDrag = event => {
    if (event.button > 0 || event.isPrimary === false) return;
    suppressClick.current = false;
    drag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      rect: event.currentTarget.getBoundingClientRect(),
      moved: false,
    };
    if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
  };

  const updateDrag = event => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moved && Math.hypot(dx, dy) < 6) return;
    current.moved = true;
    event.preventDefault();
    const { rect } = current;
    setDragOffset({
      x: Math.max(8 - rect.left, Math.min(window.innerWidth - rect.right - 8, dx)),
      y: Math.max(8 - rect.top, Math.min(window.innerHeight - rect.bottom - 8, dy)),
    });
  };

  const finishDrag = (event, cancelled = false) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragOffset(null);
    suppressClick.current = current.moved;
    if (current.moved && !cancelled) {
      moveChat(event.clientX < window.innerWidth / 2 ? 'bottom-left' : 'bottom-right');
    }
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const chatbotUrl = useMemo(() => {
    const globalConfig = window && window.global ? window.global : {};
    const envUrl = process.env.REACT_APP_COPILOT_WEBCHAT_URL || '';
    const globalUrl = globalConfig.copilotWebchatURL || '';
    const fallbackUrl = 'https://copilotstudio.microsoft.com/environments/Default-766ed5d1-7eb1-46ef-bc6f-fe163f2e86b2/bots/cr971_echo/webchat?__version__=2';
    const candidateUrl = envUrl || globalUrl || fallbackUrl;

    // Protect local dev from unresolved template placeholders like "{{ getenv ... }}".
    if (candidateUrl.includes('{{') || !candidateUrl.startsWith('http')) {
      return fallbackUrl;
    }

    return candidateUrl;
  }, []);

  if (!chatbotUrl) {
    return null;
  }

  return (
    <>
      {open && (
        <Paper
          ref={panelRef}
          elevation={6}
          className={`${classes.chatPanel} ${classes.panelBottom} ${horizontalPosition === 'left' ? classes.panelLeft : classes.panelRight}`}
        >
          <IconButton
            aria-label="Close chatbot"
            className={classes.closeButton}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <iframe
            title="Deviare Copilot Chatbot"
            src={chatbotUrl}
            className={classes.iframe}
            allow="clipboard-write"
          />
        </Paper>
      )}

      <div
        ref={launcherRef}
        style={dragOffset ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` } : undefined}
        data-chat-position={position}
        className={`${classes.hitArea} ${classes.dockBottom} ${horizontalPosition === 'left' ? classes.dockLeft : classes.dockRight} ${!open ? (horizontalPosition === 'left' ? classes.reachRight : classes.reachLeft) : ''}`}
      >
        <div className={classes.floatingControls}>
          {!open && (
            <IconButton
              className={`${classes.moveButton} ${classes.horizontalMoveButton} ${horizontalPosition === 'right' ? classes.moveLeft : classes.moveRight}`}
              aria-label={`Move chat to the ${horizontalPosition === 'right' ? 'left' : 'right'}`}
              onClick={flipHorizontally}
            >
              {horizontalPosition === 'right' ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          )}
          <IconButton
            className={classes.floatingButton}
            aria-label="Open chatbot"
            onPointerDown={startDrag}
            onPointerMove={updateDrag}
            onPointerUp={finishDrag}
            onPointerCancel={event => finishDrag(event, true)}
            onLostPointerCapture={event => finishDrag(event, true)}
            style={dragOffset ? { cursor: 'grabbing' } : undefined}
            onClick={event => {
              if (suppressClick.current && event.detail !== 0) {
                suppressClick.current = false;
                return;
              }
              setOpen(true);
            }}
          >
            <ChatIcon />
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default ChatbotWidget;
